import { useCallback, useState } from 'react';

/**
 * sysadmin 화면들이 공유하는 **기준월(YYYY-MM)** 유틸 (2026-09-02).
 *
 * ## 왜 만들었나
 *
 * '전체 고객'(`/sysadmin/client`)에서 기준월을 2026-07 로 맞춰도, 채널 하나를 눌러 상세
 * (`/sysadmin/client/:id`)로 들어가면 컴포넌트가 새로 마운트되면서 `useState(thisMonth())` 가
 * 다시 **당월**로 초기화됐다. 목록 ↔ 상세를 왕복할 때마다 기준월을 다시 입력해야 했고,
 * 더 나쁜 것은 **바뀐 걸 못 보고** 다른 달 숫자를 그 달 값으로 읽을 수 있었다는 점이다.
 *
 * 그리고 입력이 자유 텍스트라 `2026-7`·`202607` 같은 값이 들어갈 수 있었다(그 상태로 조회하면
 * 조용히 빈 결과가 나온다). 그래서 **풀다운(월 단위 고정 목록)** 으로 바꿨다 — 정산 관리 화면이
 * 이미 쓰던 방식과 같다.
 *
 * ## 저장 위치를 sessionStorage 로 정한 이유
 *
 * 기준월은 **그 순간의 작업 문맥**이지 영구 환경설정이 아니다. localStorage 에 두면 다음 날 화면을
 * 열었을 때 지난달이 선택된 채로 보이고, 그게 '최신'인지 '어제 내가 고른 값'인지 구분할 수 없다.
 * sessionStorage 는 **탭이 살아 있는 동안만**(새로고침·페이지 이동 포함) 유지되고 새 탭에서는
 * 기본값으로 돌아간다.
 *
 * ⚠️ 정산 관리(`SettlementAdmin`)는 이 훅을 쓰지 않는다 — 그 화면의 기본값은 **전월**(마감 화면이라
 * 지난달을 닫는 것이 일이다)이고, '전체 고객'의 기본값은 **당월**이다. 기본값 의미가 다른 화면끼리
 * 한 값을 공유하면 어느 쪽 의도가 맞는지 알 수 없어지므로 키를 나눠 둔다([BASE_MONTH_KEY]).
 */

/** '전체 고객' + '고객사 상세' 가 공유하는 기준월 키. 두 화면은 사실상 한 화면(목록↔상세)이다. */
export const BASE_MONTH_KEY = 'sysadmin.client.baseMonth';

/** 당월 'YYYY-MM' (브라우저 로컬 기준). */
export const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** 전월 'YYYY-MM'. */
export const prevMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * 기준월 풀다운 옵션 — 2024-01 ~ 당월, **최신이 위**.
 * 정산 관리 화면이 쓰던 `monthOptions()` 를 그대로 옮긴 것이다(같은 목록을 두 벌 두지 않는다).
 */
export const monthOptions = () => {
  const opts = [];
  const now = new Date();
  let y = now.getFullYear();
  let m = now.getMonth() + 1;
  while (y > 2024 || (y === 2024 && m >= 1)) {
    opts.push(`${y}-${String(m).padStart(2, '0')}`);
    m -= 1;
    if (m === 0) { m = 12; y -= 1; }
  }
  return opts;
};

const MONTH_RE = /^\d{4}-\d{2}$/;

const readStored = (key) => {
  try {
    const v = window.sessionStorage.getItem(key);
    // 형식이 깨진 값(수동 편집·구버전 잔재)은 무시하고 기본값으로 떨어진다.
    return v && MONTH_RE.test(v) ? v : null;
  } catch {
    // 시크릿 모드 등에서 sessionStorage 접근이 막힐 수 있다 — 그때는 그냥 유지되지 않을 뿐이다.
    return null;
  }
};

/**
 * 화면을 옮겨도 유지되는 기준월 상태. `useState` 를 그대로 대체한다.
 *
 *   const [month, setMonth] = useBaseMonth(BASE_MONTH_KEY, thisMonth());
 *
 * @param {string} key      sessionStorage 키
 * @param {string} fallback 저장값이 없을 때 쓸 기본값
 */
export const useBaseMonth = (key, fallback) => {
  const [month, setMonthState] = useState(() => readStored(key) ?? fallback);

  const setMonth = useCallback((next) => {
    setMonthState(next);
    try {
      window.sessionStorage.setItem(key, next);
    } catch {
      // 저장에 실패해도 화면 동작은 그대로 — 유지만 안 된다.
    }
  }, [key]);

  return [month, setMonth];
};
